/**
 * 뉴스 알림 서비스 API 연동 유틸리티
 * Backend crawler API와 통신하는 함수들을 제공합니다.
 */

// API 기본 URL (프록시 패턴 사용)
const getNewsApiBaseUrl = () => {
  // 개발 환경에서는 프록시 사용: /api/{server_id}/
  if (process.env.NODE_ENV === 'development') {
    return '/api/news-crawler-server/api/news';
  }
  
  // 프로덕션 환경에서는 환경변수 또는 기본값 사용
  return process.env.REACT_APP_NEWS_API_URL || 'http://localhost:8080/api/news';
};

/**
 * API 요청 헤더 생성
 * @param {string} token - JWT 토큰
 * @returns {Object} 헤더 객체
 */
const getHeaders = (token) => {
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

/**
 * API 응답 처리 유틸리티
 * @param {Response} response - fetch 응답 객체
 * @returns {Promise<Object>} 처리된 응답 데이터
 */
const handleResponse = async (response) => {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
  }
  return await response.json();
};

/**
 * 뉴스 알림 등록
 * @param {Object} alertData - 알림 데이터
 * @param {string} alertData.keyword - 검색 키워드
 * @param {string} alertData.frequency - 알림 주기 (realtime, hourly, daily, weekly, custom)
 * @param {string} alertData.alertType - 알림 방식 (email, sms, push, webhook)
 * @param {string} alertData.alertInfo - 알림 정보 (이메일, 전화번호 등)
 * @param {boolean} alertData.isActive - 활성화 상태
 * @param {Array<Object>} alertData.timeSlots - 사용자 정의 시간대 배열 (frequency가 'custom'일 때)
 * @param {string} token - 인증 토큰
 * @returns {Promise<Object>} 생성된 알림 정보
 */
export const createNewsAlert = async (alertData, token) => {
  try {
    console.log('📤 [NewsAPI] 뉴스 알림 등록 요청:', alertData);
    
    // 사용자 정의 시간대 검증
    if (alertData.frequency === 'custom') {
      if (!alertData.timeSlots || alertData.timeSlots.length === 0) {
        throw new Error('사용자 정의 주기를 선택한 경우 최소 1개 이상의 시간대를 설정해야 합니다.');
      }
      
      // 시간대 형식 검증
      for (const slot of alertData.timeSlots) {
        if (!slot.startTime || !slot.endTime) {
          throw new Error('시간대의 시작 시간과 종료 시간은 필수입니다.');
        }
        
        // 시간 형식 검증 (HH:mm)
        const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
        if (!timeRegex.test(slot.startTime) || !timeRegex.test(slot.endTime)) {
          throw new Error('시간은 HH:mm 형식이어야 합니다.');
        }
      }
    }
    
    const requestBody = {
      keyword: alertData.keyword.trim(),
      frequency: alertData.frequency,
      alert_type: alertData.alertType,
      alert_info: alertData.alertInfo.trim(),
      is_active: alertData.isActive
    };
    
    // 사용자 정의 시간대가 있는 경우 추가
    if (alertData.frequency === 'custom' && alertData.timeSlots) {
      requestBody.time_slots = alertData.timeSlots.map(slot => ({
        start_time: slot.startTime,
        end_time: slot.endTime
      }));
    }
    
    const response = await fetch(`${getNewsApiBaseUrl()}/alerts`, {
      method: 'POST',
      headers: getHeaders(token),
      body: JSON.stringify(requestBody)
    });

    const result = await handleResponse(response);
    
    console.log('✅ [NewsAPI] 뉴스 알림 등록 성공:', result);
    return result;
    
  } catch (error) {
    console.error('❌ [NewsAPI] 뉴스 알림 등록 실패:', error);
    throw error;
  }
};

/**
 * 사용자의 뉴스 알림 목록 조회
 * @param {string} token - 인증 토큰
 * @returns {Promise<Array>} 알림 목록
 */
export const getNewsAlerts = async (token) => {
  try {
    console.log('📤 [NewsAPI] 뉴스 알림 목록 조회 요청');
    
    const response = await fetch(`${getNewsApiBaseUrl()}/alerts`, {
      method: 'GET',
      headers: getHeaders(token)
    });

    const result = await handleResponse(response);
    
    console.log('✅ [NewsAPI] 뉴스 알림 목록 조회 성공:', result);
    return result.data || result;
    
  } catch (error) {
    console.error('❌ [NewsAPI] 뉴스 알림 목록 조회 실패:', error);
    throw error;
  }
};

/**
 * 뉴스 알림 삭제
 * @param {number} alertId - 알림 ID
 * @param {string} token - 인증 토큰
 * @returns {Promise<Object>} 삭제 결과
 */
export const deleteNewsAlert = async (alertId, token) => {
  try {
    console.log('📤 [NewsAPI] 뉴스 알림 삭제 요청:', alertId);
    
    const response = await fetch(`${getNewsApiBaseUrl()}/alerts/${alertId}`, {
      method: 'DELETE',
      headers: getHeaders(token)
    });

    const result = await handleResponse(response);
    
    console.log('✅ [NewsAPI] 뉴스 알림 삭제 성공:', result);
    return result;
    
  } catch (error) {
    console.error('❌ [NewsAPI] 뉴스 알림 삭제 실패:', error);
    throw error;
  }
};

/**
 * 뉴스 알림 상태 토글 (활성화/비활성화)
 * @param {number} alertId - 알림 ID
 * @param {boolean} isActive - 변경할 활성화 상태
 * @param {string} token - 인증 토큰
 * @returns {Promise<Object>} 업데이트된 알림 정보
 */
export const toggleNewsAlert = async (alertId, isActive, token) => {
  try {
    console.log('📤 [NewsAPI] 뉴스 알림 상태 변경 요청:', { alertId, isActive });
    
    const response = await fetch(`${getNewsApiBaseUrl()}/alerts/${alertId}`, {
      method: 'PATCH',
      headers: getHeaders(token),
      body: JSON.stringify({
        is_active: isActive
      })
    });

    const result = await handleResponse(response);
    
    console.log('✅ [NewsAPI] 뉴스 알림 상태 변경 성공:', result);
    return result;
    
  } catch (error) {
    console.error('❌ [NewsAPI] 뉴스 알림 상태 변경 실패:', error);
    throw error;
  }
};

/**
 * 뉴스 알림 업데이트 (키워드, 주기, 알림 정보 등)
 * @param {number} alertId - 알림 ID
 * @param {Object} updateData - 업데이트할 데이터
 * @param {string} updateData.keyword - 검색 키워드
 * @param {string} updateData.frequency - 알림 주기
 * @param {string} updateData.alertType - 알림 방식
 * @param {string} updateData.alertInfo - 알림 정보
 * @param {boolean} updateData.isActive - 활성화 상태
 * @param {Array<Object>} updateData.timeSlots - 사용자 정의 시간대 배열
 * @param {string} token - 인증 토큰
 * @returns {Promise<Object>} 업데이트된 알림 정보
 */
export const updateNewsAlert = async (alertId, updateData, token) => {
  try {
    console.log('📤 [NewsAPI] 뉴스 알림 업데이트 요청:', { alertId, updateData });
    
    // 사용자 정의 시간대 검증
    if (updateData.frequency === 'custom') {
      if (!updateData.timeSlots || updateData.timeSlots.length === 0) {
        throw new Error('사용자 정의 주기를 선택한 경우 최소 1개 이상의 시간대를 설정해야 합니다.');
      }
      
      // 시간대 형식 검증
      for (const slot of updateData.timeSlots) {
        if (!slot.startTime || !slot.endTime) {
          throw new Error('시간대의 시작 시간과 종료 시간은 필수입니다.');
        }
        
        const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
        if (!timeRegex.test(slot.startTime) || !timeRegex.test(slot.endTime)) {
          throw new Error('시간은 HH:mm 형식이어야 합니다.');
        }
      }
    }
    
    const requestBody = {};
    
    // 기본 필드들
    if (updateData.keyword) requestBody.keyword = updateData.keyword.trim();
    if (updateData.frequency) requestBody.frequency = updateData.frequency;
    if (updateData.alertType) requestBody.alert_type = updateData.alertType;
    if (updateData.alertInfo) requestBody.alert_info = updateData.alertInfo.trim();
    if (updateData.hasOwnProperty('isActive')) requestBody.is_active = updateData.isActive;
    
    // 사용자 정의 시간대가 있는 경우 추가
    if (updateData.frequency === 'custom' && updateData.timeSlots) {
      requestBody.time_slots = updateData.timeSlots.map(slot => ({
        start_time: slot.startTime,
        end_time: slot.endTime
      }));
    } else if (updateData.frequency && updateData.frequency !== 'custom') {
      // custom이 아닌 주기로 변경하는 경우 time_slots를 null로 설정
      requestBody.time_slots = null;
    }
    
    const response = await fetch(`${getNewsApiBaseUrl()}/alerts/${alertId}`, {
      method: 'PUT',
      headers: getHeaders(token),
      body: JSON.stringify(requestBody)
    });

    const result = await handleResponse(response);
    
    console.log('✅ [NewsAPI] 뉴스 알림 업데이트 성공:', result);
    return result;
    
  } catch (error) {
    console.error('❌ [NewsAPI] 뉴스 알림 업데이트 실패:', error);
    throw error;
  }
};

/**
 * 특정 알림의 크롤링 결과 조회
 * @param {number} alertId - 알림 ID
 * @param {Object} options - 조회 옵션
 * @param {number} options.limit - 결과 개수 제한 (기본값: 20)
 * @param {number} options.offset - 결과 시작 위치 (기본값: 0)
 * @param {string} options.dateFrom - 시작 날짜 (YYYY-MM-DD)
 * @param {string} options.dateTo - 종료 날짜 (YYYY-MM-DD)
 * @param {string} token - 인증 토큰
 * @returns {Promise<Object>} 크롤링 결과 데이터
 */
export const getNewsAlertResults = async (alertId, options = {}, token) => {
  try {
    const { limit = 20, offset = 0, dateFrom, dateTo } = options;
    
    const queryParams = new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString(),
      ...(dateFrom && { date_from: dateFrom }),
      ...(dateTo && { date_to: dateTo })
    });
    
    console.log('📤 [NewsAPI] 뉴스 알림 결과 조회 요청:', { alertId, options });
    
    const response = await fetch(`${getNewsApiBaseUrl()}/alerts/${alertId}/results?${queryParams}`, {
      method: 'GET',
      headers: getHeaders(token)
    });

    const result = await handleResponse(response);
    
    console.log('✅ [NewsAPI] 뉴스 알림 결과 조회 성공:', result);
    return result;
    
  } catch (error) {
    console.error('❌ [NewsAPI] 뉴스 알림 결과 조회 실패:', error);
    throw error;
  }
};

/**
 * 크롤러 상태 확인
 * @returns {Promise<Object>} 크롤러 상태 정보
 */
export const getCrawlerStatus = async () => {
  try {
    console.log('📤 [NewsAPI] 크롤러 상태 확인 요청');
    
    const response = await fetch(`${getNewsApiBaseUrl()}/crawler/status`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    const result = await handleResponse(response);
    
    console.log('✅ [NewsAPI] 크롤러 상태 확인 성공:', result);
    return result;
    
  } catch (error) {
    console.error('❌ [NewsAPI] 크롤러 상태 확인 실패:', error);
    throw error;
  }
};

/**
 * 키워드 검색 테스트 (즉시 크롤링)
 * @param {string} keyword - 검색할 키워드
 * @param {Object} options - 검색 옵션
 * @param {number} options.limit - 결과 개수 제한 (기본값: 10)
 * @param {string} token - 인증 토큰
 * @returns {Promise<Object>} 검색 결과
 */
export const testKeywordSearch = async (keyword, options = {}, token) => {
  try {
    const { limit = 10 } = options;
    
    console.log('📤 [NewsAPI] 키워드 검색 테스트 요청:', { keyword, options });
    
    const response = await fetch(`${getNewsApiBaseUrl()}/test/search`, {
      method: 'POST',
      headers: getHeaders(token),
      body: JSON.stringify({
        keyword: keyword.trim(),
        limit
      })
    });

    const result = await handleResponse(response);
    
    console.log('✅ [NewsAPI] 키워드 검색 테스트 성공:', result);
    return result;
    
  } catch (error) {
    console.error('❌ [NewsAPI] 키워드 검색 테스트 실패:', error);
    throw error;
  }
};

// 기본 내보내기
export default {
  createNewsAlert,
  getNewsAlerts,
  deleteNewsAlert,
  toggleNewsAlert,
  updateNewsAlert,
  getNewsAlertResults,
  getCrawlerStatus,
  testKeywordSearch
};